import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeReleaseOfHealthInformationPediatrician } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { FieldSelectList } from '../../../../../components/redux-form/fields/FieldSelectList';

export interface ReleaseOfHealthInformationPediatricianFormData {
    student: string;
    formId: string;
    clientFullName: string;
    clientDob: string;
    clientAddress: string;
    clientMobile: string;
    healthPlans: string;
    healthPlanOther: string;
    namePractice: string;
    namePediatrician: string;
    emails: string;
    telephone: string;
    fax: string;
    parentName: string;
    todayDate: string;
    relationToPatient: string;
}

interface ReleaseOfHealthInformationPediatricianFormProps {
    initialValues: any;
    setIsLoader: any;
    isHealthPlanOther: any;
    setIsHealthPlanOther: any;
}

type InjectedProps = InjectedFormProps<
    ReleaseOfHealthInformationPediatricianFormData,
    ReleaseOfHealthInformationPediatricianFormProps
>;

const validators: Validators<ReleaseOfHealthInformationPediatricianFormData> = {
    clientFullName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    clientDob: [required({ message: "It's required." }), maxLength({ max: 255 })],
    clientAddress: [required({ message: "It's required." }), maxLength({ max: 255 })],
    clientMobile: [required({ message: "It's required." }), maxLength({ max: 255 })],
    namePractice: [required({ message: "It's required." }), maxLength({ max: 255 })],
    namePediatrician: [required({ message: "It's required." }), maxLength({ max: 255 })],
    emails: [required({ message: "It's required." }), maxLength({ max: 255 })],
    telephone: [required({ message: "It's required." }), maxLength({ max: 255 })],
    fax: [required({ message: "It's required." }), maxLength({ max: 255 })],
    parentName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    todayDate: [required({ message: "It's required." }), maxLength({ max: 255 })],
    relationToPatient: [required({ message: "It's required." }), maxLength({ max: 255 })],
};

const ReleaseOfHealthInformationPediatricianForm: React.FunctionComponent<
    InjectedProps & ReleaseOfHealthInformationPediatricianFormProps
> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    setIsHealthPlanOther,
    isHealthPlanOther,
    initialValues,
    setIsLoader,
}: InjectedProps & ReleaseOfHealthInformationPediatricianFormProps) => {
    const healthPlansValues: SelectObject[] = [
        { label: 'Evaluations (psychological, neuropsychological, psychosocial, etc.)', value: 1 },
        { label: 'Medical History', value: 2 },
        { label: 'Progress Notes/Updates/Reports', value: 3 },
        { label: 'Psychiatric/ER Evaluations', value: 4 },
        { label: 'Educational Evaluations/School Reports', value: 5 },
        { label: 'Other', value: 6 },
    ];

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Release of Information (ROI) Authorization Form: Pediatrician/Physician</h2>
            <div></div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={14}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="clientFullName"
                type="text"
                validate={validators.clientFullName}
                wrapperConfig={{
                    label: 'Client Full Name: *',
                }}
            />
            <FieldDate
                name="clientDob"
                type="text"
                date={true}
                validate={validators.clientDob}
                wrapperConfig={{
                    label: 'Client Date Of Birth: *',
                }}
            />
            <FieldInput
                name="clientAddress"
                type="text"
                validate={validators.clientAddress}
                wrapperConfig={{
                    label: 'Client Address: *',
                }}
            />
            <FieldInput
                name="clientMobile"
                type="text"
                validate={validators.clientMobile}
                wrapperConfig={{
                    label: 'Client Mobile Phone Number: *',
                }}
            />
            <label htmlFor="healthPlans">
                I hereby authorize Manhattan Psychology Group, PC to release and/or obtain the following information in writing and/or verbally for the purpose of educational, psychiatric, and/or medical health planning:
            </label>
            <FieldSelectList
                multiple={true}
                name="healthPlans"
                data={healthPlansValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        const other = e.filter((it: { label: string; value: number }) =>
                            it.label == 'Other' ? it.value : 0,
                        );
                        if (other && other[0] !== undefined) {
                            setIsHealthPlanOther(true);
                        } else {
                            setIsHealthPlanOther(false);
                        }
                    }
                }}
            />

            {isHealthPlanOther ? (
                <FieldInput
                    name="healthPlanOther"
                    type="text"
                    validate={validators.healthPlanOther}
                    wrapperConfig={{
                        label: 'Specify Other: *',
                    }}
                />
            ) : null}

            <div>This authorization is to the specified provider written below:</div>
            <FieldInput
                name="namePractice"
                type="text"
                validate={validators.namePractice}
                wrapperConfig={{
                    label: 'Name of Practice/Institution/Agency: *',
                }}
            />
            <FieldInput
                name="namePediatrician"
                type="text"
                validate={validators.namePediatrician}
                wrapperConfig={{
                    label: 'Name of Physician/Pediatrician: *',
                }}
            />
            <FieldInput
                name="emails"
                type="text"
                validate={validators.emails}
                wrapperConfig={{
                    label: 'Email(s): *',
                }}
            />
            <FieldInput
                name="telephone"
                type="text"
                validate={validators.telephone}
                wrapperConfig={{
                    label: 'Telephone: *',
                }}
            />
            <FieldInput
                name="fax"
                type="text"
                validate={validators.fax}
                wrapperConfig={{
                    label: 'Fax: *',
                }}
            />

            <div>
                I/we hereby authorize the release and exchange of the above information with the specified provider and Manhattan Psychology Group, PC. I/we understand that this consent may be revoked at any time in writing and will expire one year from the date signed below. I understand that the above information after it has been released will be held in a confidential manner; and is not authorized for further release to a third party.
            </div>

            <FieldInput
                name="parentName"
                type="text"
                validate={validators.parentName}
                wrapperConfig={{
                    label: 'Patient/Parent or Legal Guardian Name: *',
                }}
            />
            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: 'Date: *',
                }}
            />
            <FieldInput
                name="relationToPatient"
                type="text"
                validate={validators.relationToPatient}
                wrapperConfig={{
                    label: 'Relationship to Patient: *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<
    ReleaseOfHealthInformationPediatricianFormData,
    ReleaseOfHealthInformationPediatricianFormProps
>({
    form: 'release-of-health-information-pediatrician-form',
    enableReinitialize: true,
    onSubmit: async (
        data: ReleaseOfHealthInformationPediatricianFormData,
        dispatch: Dispatch<any>,
        props: ReleaseOfHealthInformationPediatricianFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeReleaseOfHealthInformationPediatrician({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<
            ReleaseOfHealthInformationPediatricianFormData,
            ReleaseOfHealthInformationPediatricianFormProps,
            string
        >,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(ReleaseOfHealthInformationPediatricianForm);
